import { zCustom } from '@nucleus/lib-shape';
import { z } from 'zod';

export const ColorPaletteKey = {
  Color1: 'color1',
  Color2: 'color2',
  Color3: 'color3',
  Color4: 'color4',
  Color5: 'color5',
  Color6: 'color6',
  ColorLight: 'colorLight',
  ColorDark: 'colorDark',
} as const;

export const ColorConfigSchema = z.object({
  hex: z.string(),
  h: zCustom.range(0, 360, { name: 'Hue' }),
  s: zCustom.range(0, 1, { name: 'Saturation' }),
  l: zCustom.range(0, 1, { name: 'Lightness' }),
  a: zCustom.range(0, 1, { name: 'Alpha' }).optional(),
});

export type ColorConfig = z.infer<typeof ColorConfigSchema>;

export type ColorPaletteKey = (typeof ColorPaletteKey)[keyof typeof ColorPaletteKey];

export type ColorPaletteVariation =
  | 'variation-1'
  | 'variation-2'
  | 'variation-3'
  | 'variation-4'
  | 'variation-5'
  | 'variation-6';

export const BaseThemeColorKey = {
  // Base
  Dark: 'dark',
  Light: 'light',
  // Section
  SectionBackground: 'section-background',
  SectionBackgroundTexture: 'section-background-texture',
  SectionText: 'section-text',
  SectionTextHeadline: 'section-text-headline',
  SectionTextBody: 'section-text-body',
  SectionTextOverline: 'section-text-overline',
  SectionTextByline: 'section-text-byline',
  // Blocks
  BlockBackground: 'block-background',
  // Card
  CardBackground: 'card-background',
  CardText: 'card-text',
  CardTextHeadline: 'card-text-headline',
  CardTextBody: 'card-text-body',
  CardTextOverline: 'card-text-overline',
  CardTextByline: 'card-text-byline',
  // Buttons
  ButtonPrimaryBackground: 'button-primary-background',
  ButtonPrimaryText: 'button-primary-text',
  ButtonSecondaryBackground: 'button-secondary-background',
  ButtonSecondaryText: 'button-secondary-text',
  ButtonTertiaryBackground: 'button-tertiary-background',
  ButtonTertiaryText: 'button-tertiary-text',
  ButtonSecondaryAltBackground: 'button-secondary-alt-background',
  ButtonSecondaryAltText: 'button-secondary-alt-text',
  // Navigation
  NavigationBackground: 'navigation-background',
  NavigationText: 'navigation-text',
  // Logo
  Logo: 'logo',
  LogoNavigation: 'logo-navigation',
} as const;
export type BaseThemeColorKey = (typeof BaseThemeColorKey)[keyof typeof BaseThemeColorKey];

export const Tier1DerivedThemeColorKey = {
  ButtonPrimaryBackgroundHover: 'button-primary-background-hover',
  ButtonPrimaryTextHover: 'button-primary-text-hover',
  ButtonSecondaryBackgroundHover: 'button-secondary-background-hover',
  ButtonSecondaryTextHover: 'button-secondary-text-hover',
  ButtonTertiaryBackgroundHover: 'button-tertiary-background-hover',
  ButtonTertiaryTextHover: 'button-tertiary-text-hover',
  MediaBackground: 'media-background',
  HighlightBackground: 'highlight-background',
} as const;
export type Tier1DerivedThemeColorKey = (typeof Tier1DerivedThemeColorKey)[keyof typeof Tier1DerivedThemeColorKey];

export const Tier2DerivedThemeColorKey = {
  MediaBackground2: 'media-background2',
  HighlightBackground2: 'highlight-background2',
} as const;
export type Tier2DerivedThemeColorKey = (typeof Tier2DerivedThemeColorKey)[keyof typeof Tier2DerivedThemeColorKey];

export const Tier3DerivedThemeColorKey = {
  MediaBackground3: 'media-background3',
} as const;
export type Tier3DerivedThemeColorKey = (typeof Tier3DerivedThemeColorKey)[keyof typeof Tier3DerivedThemeColorKey];

export type BaseThemeColors = Record<BaseThemeColorKey, ColorPaletteKey>;
export type Tier1DerivedThemeColors = Record<Tier1DerivedThemeColorKey, BaseThemeColorKey>;
export type Tier2DerivedThemeColors = Record<Tier2DerivedThemeColorKey, Tier1DerivedThemeColorKey>;
export type Tier3DerivedThemeColors = Record<Tier3DerivedThemeColorKey, Tier2DerivedThemeColorKey>;

export const ThemeColorKey = {
  ...BaseThemeColorKey,
  ...Tier1DerivedThemeColorKey,
  ...Tier2DerivedThemeColorKey,
  ...Tier3DerivedThemeColorKey,
} as const;

export type ThemeColorKey = (typeof ThemeColorKey)[keyof typeof ThemeColorKey];

export const ThemeColorsVariationKeySchema = z.enum([
  'variation-1',
  'variation-2',
  'variation-3',
  'variation-4',
  'variation-5',
  'variation-6',
]);

export type ThemeColorsVariationKey = z.infer<typeof ThemeColorsVariationKeySchema>;

type ThemeColorsCssVariables = Record<ThemeColorKey, string | number>;
export type ThemeColorsCssVariablesByVariation = Record<ThemeColorsVariationKey, ThemeColorsCssVariables>;
